<template>
  <div
    v-if="$q.screen.lt.sm && $register"
  >
    <q-card-section
      class="row full-width justify-between items-center q-px-xs"
    >
      <q-btn-group
        flat
        class="q-pa-xs bg-responsive-grey-1 full-width"
      >
        <q-btn
          class="rounded-borders col"
          label="Split Items"
          :color="!!showItemizedSplit ? 'primary' : undefined "
          size="md"
          @click="showItemizedSplit = true; editSplitAmount = selectedOrderItemsSplitAmount;"
        />
        <q-btn
          class="rounded-borders col"
          label="Set Amount"
          :color="!!showItemizedSplit ? '' : 'primary'"
          size="md"
          @click="customSplitAmount = editSplitAmount; showItemizedSplit = false;"
        />
      </q-btn-group>
    </q-card-section>
    <q-card-section
      v-if="!showItemizedSplit"
      class="row items-center q-pb-none q-px-xs"
    >
      <q-input
        ref="splitAmountInput"
        v-model.number="editSplitAmount"
        class="full-width"
        label="Split Amount"
        :prefix="currentCircuit.localeCurrencySymbol"
        type="number"
        step="0.01"
        min="0"
        :rules="[
          val => val <= order.remainingOwed || 'Must not exceed remaining owed',
          val => val !== 0 || 'Cannot be zero',
          val => val !== null && val !== '' || 'Cannot be blank'
        ]"
      />
    </q-card-section>
    <section-heading
      class="row jusitfy-center items-center q-mb-md q-pa-none"
      heading-color="responsive-grey-7"
      heading-font-size="body2"
      heading-type="Single Line"
      title="Or split evenly"
      is-mobile-view
      customize-register-view
    />

    <q-card-section
      v-if="!showItemizedSplit"
      class="row justify-between full-width items-center q-pa-none"
    >
      <div
        v-for="splitRatioValue in [2, 3, 4, 5, 6, 8]"
        :key="splitRatioValue"
        class="col-6 text-center q-pa-xs"
      >
        <q-btn
          class="full-width"
          :label="splitRatioValue + 'x (' + computeSplitAmount(order.total, splitRatioValue) + ')'"
          :outline="splitRatio !== splitRatioValue || customSplitAmount !== computeSplitAmount(order.total, splitRatioValue)"
          color="primary"
          @click="clickSplitRatioValue(splitRatioValue)"
        />
      </div>
    </q-card-section>
    <q-btn
      label="Done"
      class="full-width"
      color="primary"
      @click="() => hide()"
    />
  </div>

  <div
    v-else-if="shown"
  >
    <q-card-section
      v-if="!showItemizedSplit"
      class="row items-center q-pb-none q-pl-xs"
    >
      <q-input
        ref="splitAmountInput"
        v-model.number="editSplitAmount"
        class="col"
        label="Split Amount"
        :prefix="currentCircuit.localeCurrencySymbol"
        type="number"
        step="0.01"
        min="0"
        :rules="[
          val => val <= order.remainingOwed || 'Must not exceed remaining owed',
          val => val !== 0 || 'Cannot be zero',
          val => val !== null && val !== '' || 'Cannot be blank'
        ]"
      />
    </q-card-section>
    <q-card-section
      v-if="!showItemizedSplit"
      class="column justify-between q-gutter-y-md q-pl-xs q-mx-xl"
    >
      <div
        v-for="splitRatioValue in [2, 3, 4, 5, 6, 8]"
        :key="splitRatioValue"
        class="col-2 full-width column items-center"
      >
        <q-btn
          class="full-width"
          :label="splitRatioValue + 'x'"
          :outline="splitRatio !== splitRatioValue || customSplitAmount !== computeSplitAmount(order.total, splitRatioValue)"
          color="primary"
          @click="clickSplitRatioValue(splitRatioValue)"
        />
        <i18n-n
          class="q-mt-xs text-grey"
          tag="span"
          :value="computeSplitAmount(order.total, splitRatioValue)"
          format="currency"
          scope="global"
        />
      </div>
    </q-card-section>
    <q-card-section
      v-if="!showItemizedSplit"
      class="row full-width justify-between items-center q-pl-xs"
    >
      <q-btn
        v-if="!restrictSplitByOrderItem"
        class="col-6"
        style="max-width: 145px;"
        label="Split Items"
        color="secondary"
        size="lg"
        @click="showItemizedSplit = true; editSplitAmount = selectedOrderItemsSplitAmount;"
      />
      <q-btn
        v-if="!restrictSplitByOrderItem"
        class="col-6"
        style="max-width: 145px;"
        label="Set Amount"
        color="primary"
        :outline="customSplitAmount !== editSplitAmount"
        size="lg"
        @click="customSplitAmount = editSplitAmount;"
      />
    </q-card-section>
    <q-card-section
      v-else
      class="column justify-evenly items-center q-pl-xs"
    >
      <q-list
        class="col-auto rounded-borders full-width"
        style="min-height: 504px; max-height: 70vh; overflow-y: auto;"
        separator
        bordered
      >
        <q-expansion-item
          dense
        >
          <template #header>
            <q-item-section>
              <q-item-label
                overline
                class="row q-gutter-x-sm"
              >
                <div class="text-uppercase q-my-auto">
                  Paid
                </div>
                <q-badge
                  color="grey-6"
                  class="col-auto"
                >
                  {{ paidItems.length }}
                </q-badge>
              </q-item-label>
            </q-item-section>
          </template>
          <q-separator />
          <q-item
            v-for="orderItem in paidItems"
            :key="orderItem.id"
            class="q-py-xs q-px-sm"
            :class="{
              'bg-grey-2': orderItem.splitRatio > 0,
              'bg-grey-4': orderItem.total === orderItem.paidAmount
            }"
            :clickable="orderItem.total !== orderItem.paidAmount"
            dense
            @click.stop="clickSplitItem(orderItem)"
          >
            <q-item-section class="col justify-evenly">
              <q-item-label
                overline
                class="text-uppercase"
              >
                {{ orderItem.type }}
              </q-item-label>
              <q-item-label>
                {{ orderItem.checkoutDescription }}
              </q-item-label>
            </q-item-section>
            <q-item-section class="col-auto text-bold">
              <i18n-n
                :value="orderItem.total"
                format="currency"
                scope="global"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>
        <q-expansion-item
          default-opened
          dense
        >
          <template #header>
            <q-item-section>
              <q-item-label
                overline
                class="row q-gutter-x-sm"
              >
                <div class="text-uppercase q-my-auto">
                  Payable
                </div>
                <q-badge
                  color="grey-6"
                  class="col-auto"
                >
                  {{ payableItems.length }}
                </q-badge>
              </q-item-label>
            </q-item-section>
          </template>
          <q-separator />
          <q-item
            v-for="orderItem in payableItems"
            :key="orderItem.id"
            class="q-py-xs q-px-sm"
            :class="{
              'bg-grey-2': orderItem.splitRatio > 0,
              'bg-grey-4': orderItem.total === orderItem.paidAmount
            }"
            :clickable="orderItem.total !== orderItem.paidAmount"
            dense
            @click.stop="clickSplitItem(orderItem)"
          >
            <q-item-section class="col justify-evenly">
              <q-item-label
                overline
                class="text-uppercase"
              >
                {{ orderItem.type }}
              </q-item-label>
              <q-item-label>
                {{ orderItem.checkoutDescription }}
              </q-item-label>
            </q-item-section>
            <q-item-section class="col-auto text-bold">
              <i18n-n
                :value="orderItem.payAmount"
                format="currency"
                scope="global"
              />
              <i18n-n
                class="text-grey"
                tag="span"
                :value="orderItem.total - orderItem.paidAmount"
                format="currency"
                scope="global"
              />
            </q-item-section>
            <q-item-section class="col-auto q-pl-md">
              <q-btn
                v-if="orderItem.payAmount === 0"
                icon="add"
                dense
                outline
                round
                color="secondary"
                @click.stop="clickSplitItem(orderItem)"
              />
              <q-btn
                v-else-if="orderItem.payAmount === (orderItem.total - orderItem.paidAmount)"
                icon="check"
                dense
                outline
                round
                color="primary"
                @click.stop="clickSplitItem(orderItem)"
              />
              <q-btn
                v-else-if="orderItem.payAmount === computeSplitAmount(orderItem.total, orderItem.splitRatio)"
                :label="orderItem.splitRatio + 'x'"
                dense
                outline
                round
                color="primary"
                @click.stop="clickSplitItem(orderItem)"
              />

              <q-btn
                v-else
                icon="tune"
                dense
                outline
                round
                color="primary"
                @click.stop="clickSplitItem(orderItem)"
              />
            </q-item-section>
          </q-item>
        </q-expansion-item>
      </q-list>
      <q-item
        class="full-width q-px-sm q-mb-md"
      >
        <q-item-section class="col justify-evenly">
          <q-item-label
            overline
            class="text-uppercase"
          >
            Split Amount
          </q-item-label>
        </q-item-section>
        <q-item-section
          class="col-auto text-bold"
          style="font-size: 16px;"
        >
          <i18n-n
            :value="selectedOrderItemsSplitAmount"
            format="currency"
            scope="global"
          />
        </q-item-section>
        <q-item-section class="col-auto q-pl-md">
          <q-btn
            icon="delete_sweep"
            dense
            outline
            round
            color="negative"
            :disable="selectedOrderItemsSplitAmount === 0"
            @click="$refs.actionDialog.show()"
          />
        </q-item-section>
      </q-item>
      <div class="row full-width justify-between items-center">
        <q-btn
          v-if="!restrictSplitByOrderItem"
          class="col-6"
          style="max-width: 145px;"
          label="Split Order"
          size="lg"
          color="secondary"
          @click="showItemizedSplit = false; splitRatio = splitRatio || $_.max([ticketCount, order.numberOfGuests || 0, 2]);"
        />
        <q-btn
          class="col-6"
          style="max-width: 145px;"
          label="Set Amount"
          color="primary"
          :outline="customSplitAmount !== editSplitAmount"
          size="lg"
          @click="customSplitAmount = editSplitAmount;"
        />
      </div>
    </q-card-section>
    <q-dialog v-model="showOrderItemRatios">
      <q-card
        v-if="selectedOrderItem"
        :style="{ 'width': '400px', 'max-width': '90%' }"
      >
        <q-card-section class="row items-center q-pb-sm">
          <div class="text-h6">
            Split Item
          </div>
          <q-space />
          <q-btn
            icon="close"
            dense
            flat
            round
            @click="showOrderItemRatios = false"
          />
        </q-card-section>
        <q-card-section
          class="row items-center q-mx-md q-pb-xs"
        >
          <q-input
            ref="splitAmountInput"
            v-model.number="selectedOrderItem.payAmount"
            class="col-8 q-pr-sm"
            label="Split Amount"
            :prefix="currentCircuit.localeCurrencySymbol"
            type="number"
            step="0.01"
            min="0"
            :rules="[
              val => val <= (selectedOrderItem.total - selectedOrderItem.paidAmount) || 'Must not exceed remaining owed',
              val => val !== 0 || 'Cannot be zero',
              val => val !== null && val !== '' || 'Cannot be blank'
            ]"
          />
          <q-btn
            v-close-popup
            class="col-4 self-start"
            style="height: 56px;"
            label="Set"
            size="lg"
            color="primary"
            :disable="!selectedOrderItem.payAmount || selectedOrderItem.payAmount <= 0 || selectedOrderItem.payAmount > (selectedOrderItem.total - selectedOrderItem.paidAmount)"
          />
        </q-card-section>
        <q-card-actions class="row justify-between q-mx-sm q-mb-sm">
          <div
            v-for="splitRatioValue in [1, 2, 3, 4, 5, 6]"
            :key="splitRatioValue"
            class="col-4 column items-center q-mb-sm"
          >
            <q-btn
              :label="splitRatioValue + 'x'"
              :outline="computeSplitAmount(selectedOrderItem.total, splitRatioValue) !== selectedOrderItem.payAmount"
              color="primary"
              @click="clickSplitItemRatio(splitRatioValue)"
            />
            <i18n-n
              class="q-mt-xs text-grey"
              tag="span"
              :value="computeSplitAmount(selectedOrderItem.total, splitRatioValue)"
              format="currency"
              scope="global"
            />
          </div>
          <div
            class="full-width q-mt-md q-px-md"
          >
            <q-btn
              class="full-width"
              icon="delete"
              label="Remove from Split"
              size="lg"
              color="secondary"
              @click="clickSplitItemRatio(0)"
            />
          </div>
        </q-card-actions>
      </q-card>
    </q-dialog>
    <action-dialog
      ref="actionDialog"
      title="Reset Split Items"
      message="Are you sure you want to reset your split item selection?"
      ok-label="Reset Split"
      ok-color="negative"
      cancel-color="secondary"
      @ok="clearSplitItems"
    />
  </div>
</template>

<script>

import ActionDialog from 'src/modules/consumer/components/ActionDialog'
import SectionHeading from 'src/modules/consumer/components/SectionHeading'

export default {
  name: 'SplitPaymentSection',
  components: {
    ActionDialog,
    SectionHeading
  },
  configuration: ['currentCircuit', 'currentSite'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    splitAmount: {
      type: Number,
      required: false,
      default: 0
    },
    order: {
      type: Object,
      required: true
    },
    restrictSplitByOrderItem: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:model-value', 'split-ratio', 'custom-split-amount'],
  data () {
    return {
      shown: false,
      showItemizedSplit: !!this.restrictSplitByOrderItem,
      editSplitAmount: null,
      customSplitAmount: null,
      splitRatio: null,
      showOrderItemRatios: false,
      selectedOrderItem: null
    }
  },
  computed: {
    ticketCount () {
      return _.filter(this.order.orderItems.data, { type: 'ticket' }).length
    },
    selectedOrderItemsSplitAmount () {
      return Math.round(_.reduce(this.order.orderItems.data, (sum, orderItem) => {
        return sum + orderItem.payAmount
      }, 0) * 100.0) / 100.0
    },
    paidItems () {
      return _.filter(this.order.orderItems.data, orderItem => {
        return orderItem.total === orderItem.paidAmount
      })
    },
    payableItems () {
      return _.filter(this.order.orderItems.data, orderItem => {
        return orderItem.total !== orderItem.paidAmount
      })
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      if (!this.showItemizedSplit && !this.customSplitAmount) {
        this.splitRatio = Math.round(this.order.total / this.splitAmount)
        this.customSplitAmount = this.splitAmount
      }
    },
    splitAmount () {
      this.customSplitAmount = this.splitAmount
    },
    splitRatio () {
      if (!this.splitRatio) return

      if (this.showItemizedSplit) return

      this.customSplitAmount = this.computeSplitAmount(this.order.total, this.splitRatio)
    },
    customSplitAmount () {
      if (!this.showItemizedSplit && this.computeSplitAmount(this.order.total, this.splitRatio) === this.customSplitAmount) {
        this.$emit('split-ratio', this.splitRatio)
        this.$emit('custom-split-amount', null)
      } else {
        this.splitRatio = null
        this.$emit('custom-split-amount', this.customSplitAmount)
        this.$emit('split-ratio', null)
      }
    },
    selectedOrderItemsSplitAmount () {
      this.editSplitAmount = this.selectedOrderItemsSplitAmount
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    hide () {
      this.shown = false
      this.$emit('update:model-value', false)
    },
    computeSplitAmount (amount, splitRatio) {
      return Math.round(amount / splitRatio * 100) / 100
    },
    clickSplitRatioValue (splitRatioValue) {
      this.splitRatio = splitRatioValue

      this.customSplitAmount = this.computeSplitAmount(this.order.total, this.splitRatio)
    },
    clickSplitItem (orderItem) {
      const newPaidTotal = (this.selectedOrderItemsSplitAmount - orderItem.payAmount + orderItem.total)
      if (newPaidTotal > this.order.remainingOwed && orderItem.payAmount > 0) {
        orderItem.payAmount = 0
        orderItem.splitRatio = 0
      } if (newPaidTotal > this.order.remainingOwed) {
        orderItem.payAmount = this.order.remainingOwed - this.selectedOrderItemsSplitAmount
        orderItem.splitRatio = 1
      } else if (orderItem.splitRatio === 0) {
        orderItem.splitRatio = 1
        orderItem.payAmount = orderItem.total - orderItem.paidAmount
      } else if (!this.restrictSplitByOrderItem) {
        this.selectedOrderItem = orderItem
        this.showOrderItemRatios = true
      }
    },
    clickSplitItemRatio (splitRatioValue) {
      this.selectedOrderItem.splitRatio = splitRatioValue

      let splitPrice = 0
      if (splitRatioValue === 1) {
        splitPrice = this.selectedOrderItem.total - this.selectedOrderItem.paidAmount
      } else if (splitRatioValue > 1) {
        splitPrice = this.selectedOrderItem.total / splitRatioValue
      }

      this.selectedOrderItem.payAmount = splitPrice

      this.showOrderItemRatios = false
    },
    clearSplitItems () {
      _.each(this.order.orderItems.data, orderItem => {
        orderItem.splitRatio = 0
        orderItem.payAmount = 0
      })
      this.customSplitAmount = 0
    }
  }
}

</script>

<style lang="scss" scoped>

h6 {
  overflow: hidden;
  text-align: center;
}

h6::before,
h6::after {
  background-color: #000;
  content: "";
  display: inline-block;
  height: 1px;
  position: relative;
  vertical-align: middle;
  width: 50%;
}

h6::before {
  right: 0.5em;
  margin-left: -50%;
}

h6::after {
  left: 0.5em;
  margin-right: -50%;
}

.rounded-borders {
  border-radius: 4px !important;
}
</style>
